import React, { useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { Box, Flex, Text } from 'rebass/styled-components';
import styled from 'styled-components';
import theme from 'styled-theming';
import { useClickAway } from 'react-use';
import { FiChevronDown, FiChevronUp } from 'react-icons/fi';
import { space, fontSize } from 'styled-system';
import DropDownItem from './DropDownItem';
import { Info } from '.';

const FONT_SIZE = 1;
const LINE_HEIGHT = '16px';
const LABEL_FONT_SIZE = '13px';

const textColor = theme('mode', {
  light: (props) => props.theme.colors.midGrey,
  dark: (props) => props.theme.colors.white,
});

const nameColor = theme('mode', {
  light: (props) => props.theme.colors.deepGrey,
  dark: (props) => (props.noText ? props.theme.colors.midLightGrey : props.theme.colors.white),
});

const backgroundColor = theme('mode', {
  light: (props) => props.theme.colors.white,
  dark: (props) => props.theme.colors.greyDarker,
});

const placeholderColor = theme('mode', {
  light: (props) => props.theme.colors.deepOrange,
  dark: (props) => props.theme.colors.orange,
});

const labelColor = theme('mode', {
  light: (props) => props.theme.colors.deepGrey,
  dark: (props) => props.theme.colors.midLightGrey,
});

const Icon = styled(FiChevronUp)`
  ${space}
  stroke-width: 1.5px;
  color: ${textColor};
`;

const Container = styled(Flex)`
  position: relative;
  background-color: ${backgroundColor};
  border-radius: ${(props) => (
    props.isOpen ? `${props.theme.radius.small} ${props.theme.radius.small} 0 0` : props.theme.radius.small
  )};
  user-select: none;
  cursor: pointer;
  overflow: ${(props) => (props.isOpen ? 'visible' : 'hidden')};
  width: 88px;
  max-width: 88px;
  max-height: 34px;
  box-sizing: border-box;
  filter: drop-shadow(1px 2px 3px rgba(0, 0, 0, 0.25));
`;

const Items = styled(Box)`
  position: absolute;
  background-color: ${backgroundColor};
  user-select: none;
  cursor: pointer;
  z-index: 1;
  top: calc(100% - 1px);
  left: 0px;
  right: 0px;
  border-radius: ${(props) => `0 0 ${props.theme.radius.medium} ${props.theme.radius.medium}`};
  overflow: auto;
  max-height: 240px;
`;

const Name = styled(Box)`
  ${space}
  ${fontSize}
  line-height: ${(props) => props.lineHeight};
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  color: ${(props) => (props.noText ? placeholderColor : nameColor)};
  flex: 1;
`;

const Label = styled(Text)`
  ${space}
  ${fontSize}
  line-height: ${(props) => props.lineHeight};
  color: ${labelColor};
  flex: 1;
  max-width: 132px;
  margin-right: 36px;
`;

function Dropdown(props) {
  const {
    items,
    value,
    selectItem,
    placeholder,
    label,
    tooltip,
    ...otherProps
  } = props;

  const ref = useRef(null);
  const [isOpen, setIsOpen] = useState(false);

  const hasItems = items.length > 0;

  useClickAway(ref, () => setIsOpen(false));

  const name = (
    <Name
      fontSize={FONT_SIZE}
      lineHeight={LINE_HEIGHT}
      p={2}
      noText={(typeof value === 'string' && value.length === 0) || (typeof value === 'boolean' && value.length === 0)}
    >
      {value}
    </Name>
  );

  return (
    <Flex>
      {label
        && (
        <Label
          fontSize={LABEL_FONT_SIZE}
          lineHeight={LINE_HEIGHT}
        >
          {label}
          {tooltip && (
            <Info tooltip={tooltip} />
          )}
        </Label>
        )}

      <Container
        isOpen={isOpen}
        justifyContent="space-between"
        ref={ref}
        onClick={hasItems ? () => { setIsOpen(!isOpen); } : undefined}
        flex={1}
        title={value}
        {...otherProps}
        data-testid="feature-toggle-dropdown"
      >
        {name}
        {hasItems && (
        <Icon
          as={isOpen ? FiChevronUp : FiChevronDown}
          m={2}
          size={16}
          onClick={(event) => {
            event.stopPropagation();
            setIsOpen(!isOpen);
          }}
        />
        )}
        {hasItems && isOpen && (
        <Items
          mb={3}
        >
          {items.map((item, index) => (
            <DropDownItem
              key={index}
              item={item}
              handleClick={(event) => {
                event.stopPropagation();
                selectItem(item);
                setIsOpen(!isOpen);
              }}
              fontSize={FONT_SIZE}
              lineHeight={LINE_HEIGHT}
            />
          ))}
        </Items>
        )}
      </Container>
    </Flex>

  );
}

Dropdown.defaultProps = {
  placeholder: 'Select a value',
  label: '',
  value: null,
  tooltip: null,
};

Dropdown.propTypes = {
  value: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.number,
    PropTypes.bool,
    PropTypes.object,
  ]),
  selectItem: PropTypes.func.isRequired,
  items: PropTypes.array.isRequired,
  placeholder: PropTypes.string,
  label: PropTypes.string,
  tooltip: PropTypes.string,
};

export default Dropdown;
